import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Box,
  IconButton,
  TextField,
  InputAdornment,
  TablePagination,
  Chip
} from '@mui/material';
import { Edit, Delete, Add, Store as StoreIcon, Search } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { storeService } from '../services/storeService';
import { Store } from '../types';
import { AppHeader } from '../components/AppHeader';

const StoreList: React.FC = () => {
  const navigate = useNavigate();
  const [stores, setStores] = useState<Store[]>([]);
  const [filteredStores, setFilteredStores] = useState<Store[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchStores();
  }, []);

  const fetchStores = async () => {
    try {
      setLoading(true);
      // A busca e a paginação desta tela são client-side sobre o dataset
      // inteiro — sem o limit explícito, o backend aplicava o default de
      // 50 e as demais lojas nunca apareciam nem na busca nem em outra
      // página (o teto do backend é 500; buscamos todas de uma vez).
      const data = await storeService.getStoresByAssociation({ limit: 500 });
      setStores(data.stores);
      setFilteredStores(data.stores);
    } catch (error) {
      console.error('Erro ao carregar lojas:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filtrar lojas com base no termo de pesquisa
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredStores(stores);
      setPage(0);
    } else {
      const filtered = stores.filter((store) => {
        const searchLower = searchTerm.toLowerCase();
        return (
          store.name?.toLowerCase().includes(searchLower) ||
          store.cnpj?.toLowerCase().includes(searchLower) ||
          store.address?.toLowerCase().includes(searchLower) ||
          store.phone?.toLowerCase().includes(searchLower) ||
          store.email?.toLowerCase().includes(searchLower)
        );
      });
      setFilteredStores(filtered);
      setPage(0);
    }
  }, [searchTerm, stores]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta loja?')) {
      try {
        await storeService.deleteStore(id);
        await fetchStores();
      } catch (error) {
        console.error('Erro ao excluir loja:', error);
      }
    }
  };

  return (
    <>
      <AppHeader 
        title="Gerenciar Lojas" 
        subtitle="Cadastre e administre lojas participantes"
        icon={<StoreIcon />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Lojas
            </Typography>
            <Chip 
              label={`${filteredStores.length} ${filteredStores.length === 1 ? 'loja' : 'lojas'}`}
              color="primary"
              size="small"
              sx={{ mt: 1 }}
            />
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/stores/new')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none'
            }}
          >
            Nova Loja
          </Button>
        </Box>

        {/* Campo de Pesquisa */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Pesquisar por nome, CNPJ, endereço, telefone ou email..."
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: 'primary.light' }}>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Nome</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>CNPJ</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Endereço</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Telefone</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'white' }}>Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Carregando...
                  </TableCell>
                </TableRow>
              ) : filteredStores.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {searchTerm ? 'Nenhuma loja encontrada com os critérios de pesquisa' : 'Nenhuma loja cadastrada'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredStores
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((store) => (
                    <TableRow 
                      key={store.id}
                      hover
                      sx={{ '&:hover': { backgroundColor: 'action.hover' } }}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>{store.name}</TableCell>
                      <TableCell>{store.cnpj}</TableCell>
                      <TableCell>{store.address}</TableCell>
                      <TableCell>{store.phone}</TableCell>
                      <TableCell>{store.email}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/stores/${store.id}/edit`)}
                          color="primary"
                          title="Editar"
                        >
                          <Edit />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(store.id)}
                          color="error"
                          title="Excluir"
                        >
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
          
          {/* Paginação */}
          {!loading && filteredStores.length > 0 && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredStores.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Lojas por página:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
              }
            />
          )}
        </TableContainer>
      </Container>
    </>
  );
}; 
export default StoreList;